import { useId } from 'react'

import { EditOutlined } from '@ant-design/icons'
import { Flex, Input } from 'antd'
import { createStyles } from 'antd-style'

import { ActionButton } from '../Button/ActionButton'
import { ConfirmInput } from '../ConfirmInput'

import { EditableInputProps } from './interface'
import { useEditableInputStore } from './store'

const useStyles = createStyles(({ token }) => ({
  container: {
    width: '100%'
  }
}))

export const EditableInput = <T extends string | number>({
  /* -------------------------------------------------------------------------- */
  /*                                 Basic Props                                */
  /* -------------------------------------------------------------------------- */
  className,
  style,

  /* -------------------------------------------------------------------------- */
  /*                             ActionButton Props                             */
  /* -------------------------------------------------------------------------- */
  uuid,
  onlyAdministrator,

  /* -------------------------------------------------------------------------- */
  /*                                 Flex Props                                 */
  /* -------------------------------------------------------------------------- */
  align = 'center',

  /* -------------------------------------------------------------------------- */
  /*                             ConfirmInput Props                             */
  /* -------------------------------------------------------------------------- */
  trigger = 'confirm',
  allowClear,
  disabled,
  value,
  getInputComponent,
  getConfirmComponent,
  getPreviewComponent,
  getActionComponent,
  onChange,
  onConfirm
}: EditableInputProps<T>) => {
  const id = useId()
  const { activeId, toggleActive } = useEditableInputStore(id)

  const handleConfirm = (value?: T) => {
    toggleActive()
    onConfirm?.(value)
  }

  const actived = id === activeId

  const customComponentProps = {
    value,
    disabled,
    toggleActive
  }

  const previewComponent = getPreviewComponent?.(customComponentProps) ?? (
    <Input disabled value={value} />
  )

  const actionComponent =
    getActionComponent?.(customComponentProps) ??
    (!disabled && (
      <ActionButton
        icon={<EditOutlined />}
        onlyAdministrator={onlyAdministrator}
        type='text'
        uuid={uuid}
        onClick={toggleActive}
      />
    ))

  const { cx, styles } = useStyles()

  return actived ? (
    <ConfirmInput
      allowClear={allowClear}
      className={cx(styles.container, className)}
      disabled={disabled}
      getConfirmComponent={getConfirmComponent}
      getInputComponent={getInputComponent}
      style={style}
      trigger={trigger}
      value={value}
      onCancel={toggleActive}
      onChange={onChange}
      onConfirm={handleConfirm}
    />
  ) : (
    <Flex
      align={align}
      className={cx(styles.container, className)}
      gap={4}
      style={style}
    >
      {previewComponent}
      {actionComponent}
    </Flex>
  )
}
